// ÁTTEKINTÉS — az Összegzés vezetői nézetének grafikonjai a három összkép-kártya ALATT.
//
//  1. BEVÉTEL vs KIADÁS, 12 hónap: a bevétel a lezárt hónapon a TÉNY (kiszámlázott), a nyitott és az
//     adat-horizont előtti hónapon a TERV — a kettő jelölve különül el, és a horizontot egy mondat kimondja.
//  2. A hónap LEGNAGYOBB bevételi forrásai (szolgáltatásonként) és kiadási kategóriái — egymás mellett, hogy a
//     „honnan jön és hova megy" egy pillantással olvasható legyen.
//
// MINDKÉT OLDAL SZŰRETLEN (a kártyákkal azonos alap): a bevétel-kimutatás szűrői a Bevétel al-nézeten hatnak.

import type { ExpenseAnalytics, RevenueForecastResponse } from '@atlas/contracts';
import { useTranslation } from 'react-i18next';

import { BarList, ChartCard } from '@/components/shared/charts';
import { Text } from '@/components/ui/text';
import { categoryLabel } from '@/features/expenses/api';
import { CashflowChart, type CashflowRow } from '@/features/finance/components/finance-charts';
import { OverviewKpis } from '@/features/finance/components/overview-kpis';
import { formatMoney, formatMoneyShort } from '@/lib/format-money';
import { formatMonthAxisLabel, formatMonthLabel } from '@/lib/month';

/** A két toplista hossza — a kártyák egy képernyőn, görgetés nélkül olvashatók maradjanak. */
const TOP_N = 8;

/**
 * A 12 hónap sorai: a bevétel a TÉNY, ahol van (lezárt hónap), különben a TERV. A két sorozat hónapjai a
 * BFF-ben ugyanabból a cél-hónapból indulnak; ha egy kiadás-hónap mégis hiányzik, 0 kiadással áll.
 */
function cashflowRows(
  forecast: RevenueForecastResponse,
  analytics: ExpenseAnalytics,
): CashflowRow[] {
  const actual = new Map(forecast.charts.actualTrend.map((a) => [a.month, a.amount]));
  const expense = new Map(analytics.trend.map((e) => [e.month, e.netAmount]));
  return forecast.charts.planTrend.map((p) => {
    const fact = actual.get(p.month) ?? null;
    return {
      month: p.month,
      revenue: fact ?? p.amount,
      revenueEstimated: fact === null,
      expense: expense.get(p.month) ?? '0',
    };
  });
}

export function OverviewPanels({
  forecast,
  analytics,
}: {
  forecast: RevenueForecastResponse | undefined;
  analytics: ExpenseAnalytics | undefined;
}): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const fmt = (v: string): string => formatMoney(v, 'HUF', locale);
  const fmtShort = (v: string): string => formatMoneyShort(v, 'HUF', locale);

  if (!forecast || !analytics) return null;

  const hasForeign = analytics.trend.some((m) => m.foreign.length > 0);
  // A LEGNAGYOBB BEVÉTELI FORRÁSOK a kártyával azonos alapon: lezárt hónapon a TÉNY szolgáltatás-bontása (a
  // besorolatlan sor a saját feliratával), nyitott hónapon a TERV — a cím kimondja, melyik.
  const closed = forecast.actual.closed;
  const topRevenueTitle = t(
    closed ? 'finance.overview.topRevenueActual' : 'finance.overview.topRevenue',
  );
  const topRevenue = (
    closed
      ? forecast.actual.byService.map((r) => ({
          name: r.name ?? t('revenueForecast.actual.unclassified'),
          amount: r.amount,
        }))
      : forecast.charts.byService.map((r) => ({ name: r.name, amount: r.amount }))
  )
    .filter((r) => Number(r.amount) !== 0)
    .slice(0, TOP_N);
  // a cél-hónap kiadás-kategóriái (a 12 havi trend UTOLSÓ oszlopa), csak a nem-nulla tételek
  const expenseTop = analytics.categoryTrend
    .map((c) => ({
      name:
        c.categoryId === null ? t('expenses.uncategorized') : categoryLabel(c.categoryName, locale),
      amount: c.values.at(-1) ?? '0',
    }))
    .filter((c) => Number(c.amount) !== 0)
    .sort((a, b) => Number(b.amount) - Number(a.amount))
    .slice(0, TOP_N);

  return (
    <div className="space-y-4">
      <OverviewKpis forecast={forecast} analytics={analytics} />
      <ChartCard
        title={t('finance.overview.cashflowTitle')}
        emptyLabel={t('revenueForecast.charts.empty')}
      >
        <CashflowChart
          rows={cashflowRows(forecast, analytics)}
          fmt={fmt}
          fmtShort={fmtShort}
          monthLabel={(m) => formatMonthAxisLabel(m, locale)}
          labels={{
            revenue: t('finance.overview.revenueActual'),
            revenuePlan: t('finance.overview.revenuePlan'),
            expense: t('finance.overview.expense'),
            balance: t('finance.overview.balance'),
            margin: t('finance.overview.margin'),
          }}
          ariaLabel={t('finance.overview.cashflowTitle')}
        />
        <Text variant="secondary" as="p" className="mt-3 text-xs">
          {/* a horizont egy régebbi BFF-válaszból hiányozhat (deploy-ablak) — akkor a mondat hónap nélkül áll */}
          {'dataHorizon' in forecast
            ? t('finance.overview.cashflowNote', {
                month: formatMonthLabel(forecast.dataHorizon.actualsFrom, locale),
              })
            : t('finance.overview.cashflowNoteNoHorizon')}
          {hasForeign && ` ${t('finance.overview.foreignNote')}`}
        </Text>
      </ChartCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title={topRevenueTitle}
          emptyLabel={t('revenueForecast.charts.empty')}
          isEmpty={topRevenue.length === 0}
        >
          <BarList data={topRevenue} fmt={fmt} ariaLabel={topRevenueTitle} />
        </ChartCard>
        <ChartCard
          title={t('finance.overview.topExpense')}
          emptyLabel={t('finance.summary.byCategoryEmpty')}
          isEmpty={expenseTop.length === 0}
        >
          <BarList data={expenseTop} fmt={fmt} ariaLabel={t('finance.overview.topExpense')} />
        </ChartCard>
      </div>
    </div>
  );
}
